// v2.22 file master: SHA-256 computed piece by piece while the file is read (no imports, so Jest can test it).
// The browser's own crypto.subtle.digest needs the whole file in memory at once (up to 1 GB), so the design
// file is hashed in parts here instead. The hash is only a fingerprint for "same file again" and resume —
// security does not depend on it (the server cannot recompute it, the file never passes the server).

const K = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);

export class Sha256 {
  constructor() {
    this.h = new Int32Array([0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19]);
    this.w = new Int32Array(64);
    this.sisa = new Uint8Array(64); // bytes waiting for a full 64-byte block
    this.nSisa = 0;
    this.panjang = 0; // total bytes (Number is exact up to 2^53, far above any file size here)
  }

  _blok(b, o) {
    const w = this.w, h = this.h;
    for (let i = 0; i < 16; i++) {
      const j = o + i * 4;
      w[i] = (b[j] << 24) | (b[j + 1] << 16) | (b[j + 2] << 8) | b[j + 3];
    }
    for (let i = 16; i < 64; i++) {
      const a = w[i - 15], c = w[i - 2];
      const s0 = ((a >>> 7) | (a << 25)) ^ ((a >>> 18) | (a << 14)) ^ (a >>> 3);
      const s1 = ((c >>> 17) | (c << 15)) ^ ((c >>> 19) | (c << 13)) ^ (c >>> 10);
      w[i] = (w[i - 16] + s0 + w[i - 7] + s1) | 0;
    }
    let A = h[0], B = h[1], C = h[2], D = h[3], E = h[4], F = h[5], G = h[6], H = h[7];
    for (let i = 0; i < 64; i++) {
      const S1 = ((E >>> 6) | (E << 26)) ^ ((E >>> 11) | (E << 21)) ^ ((E >>> 25) | (E << 7));
      const t1 = (H + S1 + ((E & F) ^ (~E & G)) + K[i] + w[i]) | 0;
      const S0 = ((A >>> 2) | (A << 30)) ^ ((A >>> 13) | (A << 19)) ^ ((A >>> 22) | (A << 10));
      const t2 = (S0 + ((A & B) ^ (A & C) ^ (B & C))) | 0;
      H = G; G = F; F = E; E = (D + t1) | 0; D = C; C = B; B = A; A = (t1 + t2) | 0;
    }
    h[0] = (h[0] + A) | 0; h[1] = (h[1] + B) | 0; h[2] = (h[2] + C) | 0; h[3] = (h[3] + D) | 0;
    h[4] = (h[4] + E) | 0; h[5] = (h[5] + F) | 0; h[6] = (h[6] + G) | 0; h[7] = (h[7] + H) | 0;
  }

  update(data) {
    const b = data instanceof Uint8Array ? data : new Uint8Array(data);
    let i = 0;
    this.panjang += b.length;
    if (this.nSisa) {
      while (this.nSisa < 64 && i < b.length) this.sisa[this.nSisa++] = b[i++];
      if (this.nSisa < 64) return this;
      this._blok(this.sisa, 0);
      this.nSisa = 0;
    }
    for (; i + 64 <= b.length; i += 64) this._blok(b, i);
    while (i < b.length) this.sisa[this.nSisa++] = b[i++];
    return this;
  }

  hex() {
    const bit = this.panjang * 8;
    const pad = new Uint8Array(((this.nSisa < 56 ? 56 : 120) - this.nSisa) + 8);
    pad[0] = 0x80;
    const n = pad.length;
    const tinggi = Math.floor(bit / 0x100000000), rendah = bit >>> 0;
    pad[n - 8] = tinggi >>> 24; pad[n - 7] = (tinggi >>> 16) & 255; pad[n - 6] = (tinggi >>> 8) & 255; pad[n - 5] = tinggi & 255;
    pad[n - 4] = rendah >>> 24; pad[n - 3] = (rendah >>> 16) & 255; pad[n - 2] = (rendah >>> 8) & 255; pad[n - 1] = rendah & 255;
    const panjang = this.panjang;
    this.update(pad);
    this.panjang = panjang;
    let out = "";
    for (let i = 0; i < 8; i++) out += (this.h[i] >>> 0).toString(16).padStart(8, "0");
    return out;
  }
}

// whole buffer at once (tests and small inputs)
export function sha256Hex(data) {
  return new Sha256().update(data).hex();
}
